<template>
    <themed-modal
        name="tour-results-modal"
        title="Tour Results"
        size="xl"
        :show-footer="false"
        @closed="closeModal"
    >
        <!-- Tour Overview Section -->
        <div class="tour-results__overview">
            <div class="tour-results__header">
                <div class="tour-results__title-section">
                    <h2 class="tour-results__tour-name">{{ tour.name }}</h2>
                    <span 
                        class="tour-results__status-badge"
                        :class="`tour-results__status-badge--${tour.status}`"
                    >
                        {{ formatStatus(tour.status) }}
                    </span>
                </div>
                <p v-if="tour.description" class="tour-results__description">
                    {{ tour.description }}
                </p>
            </div>

            <div class="tour-results__meta">
                <div class="tour-results__meta-item">
                    <span class="tour-results__meta-label">Date:</span>
                    <span class="tour-results__meta-value">{{ formatDate(tour.date) }}</span>
                </div>
                <div class="tour-results__meta-item">
                    <span class="tour-results__meta-label">Attendees:</span>
                    <span class="tour-results__meta-value">{{ tour.attendees?.length || 0 }}</span>
                </div>
                <div class="tour-results__meta-item">
                    <span class="tour-results__meta-label">Locations:</span>
                    <span class="tour-results__meta-value">{{ tour.tour_comps?.length || 0 }}</span>
                </div>
            </div>
        </div>

        <!-- Results Summary -->
        <div class="results-summary">
            <div class="summary-card">
                <div class="summary-card__value">{{ results.length }}</div>
                <div class="summary-card__label">Comps Scored</div>
            </div>
            <div class="summary-card">
                <div class="summary-card__value">{{ attendeeCount }}</div>
                <div class="summary-card__label">Participants</div>
            </div>
            <div class="summary-card">
                <div class="summary-card__value">{{ progressPercentage }}%</div>
                <div class="summary-card__label">Complete</div>
            </div>
        </div>

        <themed-tabs v-model="currentView" :tabs="tabsConfig">
            <template #default="{ activeTab }">
                <div v-if="activeTab === 'rankings'" class="rankings-view">
                    <h4>Comp Rankings</h4>
                    <themed-table
                        :columns="rankingsColumns"
                        :data="rankingsTableData"
                        :loading="loading"
                    />
                </div>

                <div v-else-if="activeTab === 'detailed'" class="detailed-view">
                    <h4>Detailed Breakdown</h4>
                    <div
                        v-for="result in sortedResults"
                        :key="result.comp_id"
                        class="comp-breakdown"
                    >
                        <div class="comp-breakdown__header" @click="toggleCompDetails(result.comp_id)">
                            <h5>{{ getCompName(result.comp_id) }}</h5>
                            <span class="comp-breakdown__score">
                                Score: {{ formatScore(result.total_weighted_score) }}
                            </span>
                            <span class="comp-breakdown__toggle">
                                {{ expandedComps.includes(result.comp_id) ? '▼' : '▶' }}
                            </span>
                        </div>
                        
                        <div v-if="expandedComps.includes(result.comp_id)" class="comp-breakdown__details">
                            <div v-if="loadingDetails[result.comp_id]" class="loading">
                                Loading details...
                            </div>
                            <div v-else-if="compDetails[result.comp_id]" class="participant-scores">
                                <div
                                    v-for="(userScores, userId) in compDetails[result.comp_id].participant_scores"
                                    :key="userId"
                                    class="participant-score-group"
                                >
                                    <h6>{{ getUserName(userScores[0]) }}</h6>
                                    <div class="ksd-scores">
                                        <div
                                            v-for="score in userScores"
                                            :key="score.id"
                                            class="ksd-score-item"
                                        >
                                            <span class="ksd-name">{{ score.ksd.driver }}</span>
                                            <span class="ksd-score">{{ score.score }}</span>
                                            <span class="ksd-calculated">({{ formatScore(score.calculated_score) }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="activeTab === 'participants'" class="participants-view">
                    <h4>Scores by Participant</h4>
                    <div
                        v-for="(scores, userId) in participantScores"
                        :key="userId"
                        class="participant-section"
                    >
                        <h5>{{ getUserName(scores[0]) }}</h5>
                        <div class="participant-comps">
                            <div
                                v-for="compGroup in groupByComp(scores)"
                                :key="compGroup.comp_id"
                                class="participant-comp"
                            >
                                <div class="participant-comp__name">
                                    {{ getCompName(compGroup.comp_id) }}
                                </div>
                                <div class="participant-comp__scores">
                                    <div
                                        v-for="score in compGroup.scores"
                                        :key="score.id"
                                        class="mini-score"
                                    >
                                        <span>{{ score.ksd.driver }}: {{ score.score }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="activeTab === 'media'" class="media-view">
                    <div v-if="loadingMedia" class="loading">
                        Loading media...
                    </div>
                    <div v-else-if="mediaData">
                        <div class="media-summary">
                            <div class="media-summary__stat">
                                <span class="media-summary__count">{{ mediaData.summary.photos_count }}</span>
                                <span class="media-summary__label">Photos</span>
                            </div>
                            <div class="media-summary__stat">
                                <span class="media-summary__count">{{ mediaData.summary.videos_count }}</span>
                                <span class="media-summary__label">Videos</span>
                            </div>
                            <div class="media-summary__stat">
                                <span class="media-summary__count">{{ mediaData.summary.notes_count }}</span>
                                <span class="media-summary__label">Notes</span>
                            </div>
                        </div>

                        <div v-if="mediaData.by_location.length === 0" class="empty-state">
                            <p>No media or comments have been added to this tour yet.</p>
                        </div>

                        <div
                            v-for="location in mediaData.by_location"
                            :key="location.tour_comp_id"
                            class="location-media"
                        >
                            <div class="location-media__header">
                                <h5>{{ location.location_name }}</h5>
                                <span class="location-media__count">{{ location.total_count }} items</span>
                            </div>

                            <!-- Photos -->
                            <div v-if="location.photos.length > 0" class="media-section">
                                <h6 class="media-section__title">Photos ({{ location.photos.length }})</h6>
                                <div class="photo-grid">
                                    <div
                                        v-for="photo in location.photos"
                                        :key="photo.id"
                                        class="photo-item"
                                    >
                                        <img :src="photo.file_url" :alt="photo.caption || 'Tour photo'" />
                                        <div class="photo-item__info">
                                            <p v-if="photo.caption" class="photo-item__caption">{{ photo.caption }}</p>
                                            <p class="photo-item__meta">
                                                By {{ getMediaAuthor(photo) }} • {{ formatMediaDate(photo.captured_at || photo.created_at) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Videos -->
                            <div v-if="location.videos.length > 0" class="media-section">
                                <h6 class="media-section__title">Videos ({{ location.videos.length }})</h6>
                                <div class="video-list">
                                    <div
                                        v-for="video in location.videos"
                                        :key="video.id"
                                        class="video-item"
                                    >
                                        <video :src="video.file_url" controls></video>
                                        <div class="video-item__info">
                                            <p v-if="video.caption" class="video-item__caption">{{ video.caption }}</p>
                                            <p class="video-item__meta">
                                                By {{ getMediaAuthor(video) }} • {{ formatMediaDate(video.captured_at || video.created_at) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Notes -->
                            <div v-if="location.notes.length > 0" class="media-section">
                                <h6 class="media-section__title">Notes & Comments ({{ location.notes.length }})</h6>
                                <div class="notes-list">
                                    <div
                                        v-for="note in location.notes"
                                        :key="note.id"
                                        class="note-item"
                                    >
                                        <div class="note-item__header">
                                            <span class="note-item__author">{{ getMediaAuthor(note) }}</span>
                                            <span class="note-item__date">{{ formatMediaDate(note.captured_at || note.created_at) }}</span>
                                        </div>
                                        <p class="note-item__content">{{ note.content }}</p>
                                        <p v-if="note.caption" class="note-item__caption">{{ note.caption }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </themed-tabs>

        <div class="export-section">
            <themed-button
                @click="exportResults"
                variant="secondary"
                :disabled="exporting"
            >
                {{ exporting ? 'Exporting...' : 'Export Results' }}
            </themed-button>
        </div>
    </themed-modal>
</template>

<script>
import axios from 'axios';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedTabs from '../ThemedTabs.vue';
import ThemedTable from '../ThemedTable.vue';

export default {
    name: 'TourResults',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedTabs,
        ThemedTable,
    },

    props: {
        tour: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            results: [],
            participantScores: {},
            compDetails: {},
            loadingDetails: {},
            expandedComps: [],
            currentView: 'rankings',
            loading: false,
            exporting: false,
            attendeeCount: 0,
            progressPercentage: 0,
            mediaData: null,
            loadingMedia: false,
            selectedLocation: null,
        };
    },

    computed: {
        sortedResults() {
            return [...this.results].sort((a, b) => 
                b.total_weighted_score - a.total_weighted_score
            );
        },

        tabsConfig() {
            return [
                { label: 'Rankings', value: 'rankings' },
                { label: 'Detailed Scores', value: 'detailed' },
                { label: 'By Participant', value: 'participants' },
                { label: 'Media & Comments', value: 'media' },
            ];
        },

        rankingsColumns() {
            return [
                {
                    field: 'rank',
                    label: 'Rank',
                    sortable: false,
                    width: '80px',
                },
                {
                    field: 'comp_name',
                    label: 'Comp',
                    sortable: true,
                },
                {
                    field: 'total_weighted_score',
                    label: 'Total Score',
                    sortable: true,
                    align: 'center',
                    width: '150px',
                },
                {
                    field: 'participant_count',
                    label: 'Participants',
                    sortable: true,
                    align: 'center',
                    width: '150px',
                }
            ];
        },

        rankingsTableData() {
            return this.sortedResults.map((result, index) => {
                const rank = index + 1;
                const badges = {
                    1: '🥇',
                    2: '🥈',
                    3: '🥉'
                };
                
                return {
                    rank: badges[rank] || rank,
                    comp_name: this.getCompName(result.comp_id),
                    total_weighted_score: this.formatScore(result.total_weighted_score),
                    participant_count: result.participant_count || 0,
                    _raw: result
                };
            });
        },
    },

    mounted() {
        this.$modal.show('tour-results-modal');
        if (this.tour) {
            this.loadResults();
            this.loadProgress();
            this.loadMedia();
        }
    },

    methods: {
        closeModal() {
            this.$modal.hide('tour-results-modal');
        },
        
        async loadResults() {
            this.loading = true;
            try {
                const [resultsResponse, participantsResponse] = await Promise.all([
                    axios.get(`/tours/${this.tour.id}/results`),
                    axios.get(`/tours/${this.tour.id}/results/participants`),
                ]);
                
                this.results = resultsResponse.data.results || [];
                this.participantScores = participantsResponse.data || {};
                
                // Update tour data with loaded relationships
                if (resultsResponse.data.tour) {
                    // TourManager owns selectedTour; let it apply the update.
                    this.$emit('tour-updated', { ...this.tour, ...resultsResponse.data.tour });
                }
            } catch (error) {
                console.error('Error loading results:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load results');
                }
            } finally {
                this.loading = false;
            }
        },

        async loadProgress() {
            try {
                const response = await axios.get(`/tours/${this.tour.id}/progress`);
                this.attendeeCount = response.data.total_attendees;
                this.progressPercentage = response.data.completion_percentage;
            } catch (error) {
                console.error('Error loading progress:', error);
            }
        },

        async toggleCompDetails(compId) {
            const index = this.expandedComps.indexOf(compId);
            if (index > -1) {
                this.expandedComps.splice(index, 1);
            } else {
                this.expandedComps.push(compId);
                if (!this.compDetails[compId]) {
                    await this.loadCompDetails(compId);
                }
            }
        },

        async loadCompDetails(compId) {
            this.loadingDetails[compId] = true;
            try {
                const response = await axios.get(`/tours/${this.tour.id}/results/comps/${compId}`);
                this.$set(this.compDetails, compId, response.data);
            } catch (error) {
                console.error('Error loading comp details:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load comp details');
                }
            } finally {
                this.loadingDetails[compId] = false;
            }
        },

        async exportResults() {
            this.exporting = true;
            try {
                const response = await axios.get(`/tours/${this.tour.id}/results/export`);
                
                const dataStr = JSON.stringify(response.data, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `tour-${this.tour.id}-results.json`;
                link.click();
                URL.revokeObjectURL(url);
                
                if (this.$toast) {
                    this.$toast.success('Results exported successfully');
                }
            } catch (error) {
                console.error('Error exporting results:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to export results');
                }
            } finally {
                this.exporting = false;
            }
        },

        getCompName(compId) {
            const tourComp = this.tour.tour_comps?.find(tc => tc.comp_id === compId);
            
            if (!tourComp) {
                return 'Unnamed Comp';
            }
            
            // Try to get building from comp relationship first, then direct building
            const building = tourComp.comp?.building || tourComp.building;
            
            if (!building) {
                return 'Unnamed Comp';
            }
            
            return building.building_name || building.address_line_1 || 'Unnamed Comp';
        },

        formatScore(score) {
            if (score === null || score === undefined || isNaN(score)) {
                return '0.00';
            }
            return Number(score).toFixed(2);
        },

        getUserName(score) {
            // Handle attendee relationship
            if (score?.attendee) {
                return score.attendee.display_name || score.attendee.attendee_name || score.attendee.attendee_email || 'Unknown';
            }
            // Fallback to user if available
            return score?.user?.name || 'Unknown User';
        },

        groupByComp(scores) {
            const grouped = {};
            scores.forEach(score => {
                if (!grouped[score.comp_id]) {
                    grouped[score.comp_id] = {
                        comp_id: score.comp_id,
                        scores: [],
                    };
                }
                grouped[score.comp_id].scores.push(score);
            });
            return Object.values(grouped);
        },

        formatStatus(status) {
            const statuses = {
                draft: 'Draft',
                active: 'Active',
                completed: 'Completed',
                cancelled: 'Cancelled',
            };
            return statuses[status] || status;
        },

        formatDate(date) {
            if (!date) return 'Not scheduled';
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        async loadMedia() {
            this.loadingMedia = true;
            try {
                const response = await axios.get(`/tours/${this.tour.id}/results/media`);
                this.mediaData = response.data;
            } catch (error) {
                console.error('Error loading media:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load media');
                }
            } finally {
                this.loadingMedia = false;
            }
        },

        getMediaAuthor(media) {
            if (media.attendee) {
                return media.attendee.display_name || media.attendee.attendee_name || media.attendee.attendee_email || 'Unknown';
            }
            return media.user?.name || 'Unknown';
        },

        formatMediaDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
    },
};
</script>
